import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useConsultation } from "../context/ConsultationContext";
import { SERVICE_OPTIONS } from "../data/services";
import { FIRM, TEL_LINK, waLink, WA_MESSAGES } from "../data/firm";
import { Phone, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  full_name: "",
  mobile: "",
  email: "",
  service: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

export const ConsultationModal = () => {
  const { open, setOpen, presetService } = useConsultation();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setServerError("");
      setErrors({});
      setForm({ ...emptyForm, service: presetService || "" });
    }
  }, [open, presetService]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.full_name.trim().length < 2) errs.full_name = "Please enter your full name";
    if (!/^[0-9+\-\s]{8,20}$/.test(form.mobile.trim())) errs.mobile = "Please enter a valid mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Please enter a valid email address";
    if (!form.service) errs.service = "Please select a service";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");
    try {
      await axios.post(`${API}/consultations`, form);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setServerError(
        typeof detail === "string" ? detail : "Something went wrong. Please try again or reach us on WhatsApp."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:border-corpblue focus:outline-none transition-colors";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-lg rounded-none border-slate-200 p-0 max-h-[90vh] overflow-y-auto"
        data-testid="consultation-modal"
        data-lenis-prevent
      >
        {submitted ? (
          <div className="p-8 text-center" data-testid="consultation-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-corpblue" strokeWidth={1.5} />
            <h3 className="mt-4 font-display text-2xl font-semibold text-navy">Thank you.</h3>
            <p className="mt-2 text-sm text-slate-600">
              Your consultation request has been received. Our team will get in touch with you shortly.
            </p>
            <p className="mt-4 text-xs text-slate-500">Need an immediate response?</p>
            <div className="mt-3 flex justify-center gap-3">
              <a
                data-testid="consultation-success-whatsapp"
                href={waLink(WA_MESSAGES.consultation)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1eb85a]"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <a
                data-testid="consultation-success-call"
                href={TEL_LINK}
                className="inline-flex items-center gap-2 bg-navy px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-800"
              >
                <Phone className="h-4 w-4" /> Call Us
              </a>
            </div>
          </div>
        ) : (
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-semibold text-navy">Get Consultation</DialogTitle>
              <DialogDescription className="text-sm text-slate-600">
                Share your requirement and the team at {FIRM.name} will respond.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
              <div>
                <label htmlFor="c-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Full Name *
                </label>
                <input id="c-name" data-testid="consultation-name-input" className={inputCls} value={form.full_name} onChange={set("full_name")} autoComplete="name" />
                {errors.full_name && <p className="mt-1 text-xs text-red-600" role="alert">{errors.full_name}</p>}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="c-mobile" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Mobile *
                  </label>
                  <input id="c-mobile" data-testid="consultation-mobile-input" type="tel" className={inputCls} value={form.mobile} onChange={set("mobile")} autoComplete="tel" />
                  {errors.mobile && <p className="mt-1 text-xs text-red-600" role="alert">{errors.mobile}</p>}
                </div>
                <div>
                  <label htmlFor="c-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Email *
                  </label>
                  <input id="c-email" data-testid="consultation-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
                  {errors.email && <p className="mt-1 text-xs text-red-600" role="alert">{errors.email}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="c-service" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Service Required *
                </label>
                <select id="c-service" data-testid="consultation-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                  <option value="">Select a service</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {errors.service && <p className="mt-1 text-xs text-red-600" role="alert">{errors.service}</p>}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="c-date" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Preferred Date
                  </label>
                  <input id="c-date" data-testid="consultation-date-input" type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
                </div>
                <div>
                  <label htmlFor="c-time" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Preferred Time
                  </label>
                  <input id="c-time" data-testid="consultation-time-input" type="time" className={inputCls} value={form.preferred_time} onChange={set("preferred_time")} />
                </div>
              </div>
              <div>
                <label htmlFor="c-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Message
                </label>
                <textarea id="c-message" data-testid="consultation-message-input" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Briefly describe your requirement" />
              </div>
              {serverError && (
                <p className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="consultation-error">
                  {serverError}
                </p>
              )}
              <button
                type="submit"
                data-testid="consultation-submit-button"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 bg-corpblue px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-navy disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? "Submitting..." : "Book Consultation"}
              </button>
              <p className="text-center text-[11px] leading-relaxed text-slate-400">
                Your information is kept confidential and used only to respond to your request.
              </p>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
